import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { api } from '../api'
import { groupUnitsForPicker } from '../units'
import { ModalShell } from './ModalShell'
import type { IngredientRow } from '../types'

type Props = {
  open: boolean
  ingredient: IngredientRow | null
  onCancel: () => void
  onSaved: (row: IngredientRow) => void
  zIndex?: number
}

export function IngredientEditModal({ open, ingredient, onCancel, onSaved, zIndex = 60 }: Props) {
  const [name, setName] = useState(ingredient?.name ?? '')
  const [unit, setUnit] = useState(ingredient?.default_unit || 'g')
  const [notes, setNotes] = useState('')
  const [purchaseQty, setPurchaseQty] = useState(
    ingredient?.purchase_qty != null ? String(ingredient.purchase_qty) : ''
  )
  const [purchaseUnit, setPurchaseUnit] = useState(ingredient?.purchase_unit || '')
  const [targetQty, setTargetQty] = useState(
    ingredient?.target_qty != null ? String(ingredient.target_qty) : ''
  )
  const [shelfLife, setShelfLife] = useState(
    ingredient?.shelf_life != null ? String(ingredient.shelf_life) : ''
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Retain the last ingredient during fade-out so the panel keeps rendering content.
  const [cachedIng, setCachedIng] = useState<IngredientRow | null>(null)
  useEffect(() => { if (ingredient) setCachedIng(ingredient) }, [ingredient])
  const liveIng = ingredient ?? cachedIng
  if (!liveIng) {
    return <ModalShell open={false} onClose={onCancel} zIndex={zIndex}>{null}</ModalShell>
  }
  const readOnly = !liveIng.owned
  // Caller should pass key={ingredient.id} so this component remounts (and
  // re-runs useState initializers) when a different row is opened.

  const save = async () => {
    setError(null)
    if (!name.trim()) { setError('Name required.'); return }
    setSaving(true)
    try {
      const pq = purchaseQty.trim()
      const tq = targetQty.trim()
      const sl = shelfLife.trim()
      if (!ingredient) return
      if (tq && !purchaseUnit) {
        setError('Set a purchase unit before setting an on-hand target.')
        setSaving(false)
        return
      }
      const row = await api.updateIngredient(ingredient.id, {
        name: name.trim(), default_unit: unit, notes: notes.trim() || undefined,
        purchase_qty: pq ? Number(pq) : null,
        purchase_unit: (pq || tq) && purchaseUnit ? purchaseUnit : null,
        target_qty: tq ? Number(tq) : null,
        shelf_life: sl ? Number(sl) : null,
      })
      onSaved(row)
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  const grouped = groupUnitsForPicker()

  return (
    <ModalShell
      open={open && !!ingredient}
      onClose={onCancel}
      zIndex={zIndex}
      autoHeight
      panelStyle={{ width: 'min(420px, 100%)', padding: '18px 20px', gap: 14 }}
    >
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>{readOnly ? 'Catalog ingredient' : 'Edit ingredient'}</div>
          <div style={{
            fontFamily: theme.serif, fontSize: 22, fontWeight: theme.headWeight,
            color: theme.ink, marginTop: 2,
          }}>{liveIng.name}</div>
        </div>

        {readOnly && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: theme.surface2, color: theme.inkSoft,
            fontFamily: theme.sans, fontSize: 12,
          }}>Catalog ingredients are read-only for now.</div>
        )}

        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: theme.radius,
            background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
            fontFamily: theme.sans, fontSize: 12,
          }}>{error}</div>
        )}

        <Field label="Name">
          <input value={name} disabled={readOnly}
            onChange={e => setName(e.target.value)} style={inputStyle}/>
        </Field>

        <Field label="Default unit">
          <select value={unit} disabled={readOnly}
            onChange={e => setUnit(e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
            <optgroup label="Mass">
              {grouped.mass.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </optgroup>
            <optgroup label="Volume">
              {grouped.volume.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </optgroup>
            <optgroup label="Count">
              {grouped.count.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
            </optgroup>
          </select>
        </Field>

        <Field label="Typical purchase (optional)">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number" inputMode="decimal" min="0" step="any"
              value={purchaseQty} disabled={readOnly}
              onChange={e => setPurchaseQty(e.target.value)}
              placeholder="qty" style={{ ...inputStyle, flex: 1 }}/>
            <select value={purchaseUnit} disabled={readOnly}
              onChange={e => setPurchaseUnit(e.target.value)}
              style={{ ...inputStyle, appearance: 'auto', flex: 1 }}>
              <option value="">—</option>
              <optgroup label="Mass">
                {grouped.mass.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
              </optgroup>
              <optgroup label="Volume">
                {grouped.volume.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
              </optgroup>
              <optgroup label="Count">
                {grouped.count.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
              </optgroup>
            </select>
          </div>
        </Field>

        <Field label={`Keep on hand (optional${purchaseUnit ? `, ${purchaseUnit}` : ''})`}>
          <input
            type="number" inputMode="decimal" min="0" step="any"
            value={targetQty} disabled={readOnly || !purchaseUnit}
            onChange={e => setTargetQty(e.target.value)}
            placeholder={purchaseUnit ? 'qty to maintain' : 'set a purchase unit first'}
            style={inputStyle}/>
        </Field>

        <Field label="Shelf life (days, optional)">
          <input
            type="number" inputMode="numeric" min="0" step="1"
            value={shelfLife} disabled={readOnly}
            onChange={e => setShelfLife(e.target.value)}
            placeholder="e.g. 14" style={inputStyle}/>
        </Field>

        <Field label="Notes (optional)">
          <input value={notes} disabled={readOnly}
            onChange={e => setNotes(e.target.value)} style={inputStyle}/>
        </Field>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onCancel} style={btnSecondary}>Close</button>
          {!readOnly && (
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
    </ModalShell>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>{label}</span>
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  all: 'unset',
  boxSizing: 'border-box',
  width: '100%',
  padding: '7px 10px',
  borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`,
  background: theme.bg,
  fontFamily: theme.sans,
  fontSize: 13,
  color: theme.ink,
}

const btnSecondary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.ink,
  border: `1px solid ${theme.lineStrong}`,
}

const btnPrimary: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  padding: '8px 14px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
  textTransform: 'uppercase', color: theme.surface,
  background: theme.ink,
}
